import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="w-full max-w-md" data-testid="forgot-password-page">
          {sent ? (
            <div className="bg-card border border-border rounded-sm p-8 text-center">
              <CheckCircle className="h-16 w-16 text-[#78BE20] mx-auto" />
              <h1 className="text-2xl font-black text-foreground uppercase tracking-tight mt-4">Check Your Email</h1>
              <p className="text-muted-foreground mt-3">
                If an account exists for <strong className="text-foreground">{email}</strong>, we've sent a password reset link. Please check your inbox and spam folder.
              </p>
              <p className="text-muted-foreground text-sm mt-4">The link will expire in 1 hour.</p>
              <Link to="/login">
                <Button className="mt-6 bg-[#78BE20] text-black hover:bg-[#65a31b]">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back to Login
                </Button>
              </Link>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-sm p-8">
              <h1 className="text-2xl font-black text-foreground uppercase tracking-tight text-center">Reset Password</h1>
              <p className="text-muted-foreground text-center mt-2">Enter your email and we'll send you a reset link</p>

              {error && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-red-400 text-sm">{error}</div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email" type="email" required value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="forgot-submit-btn">
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/login" className="text-sm text-[#78BE20] hover:underline">
                  <ArrowLeft className="h-3 w-3 inline mr-1" /> Back to Login
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
